import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './i18n/I18nContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import Login from './pages/Login';
import AdminDashboard from './pages/admin/Dashboard';
import UsersManager from './pages/admin/UsersManager';
import BusesManager from './pages/admin/BusesManager';
import RoutesManager from './pages/admin/RoutesManager';
import StudentsManager from './pages/admin/StudentsManager';
import TripHistory from './pages/admin/TripHistory';
import LiveTracking from './pages/admin/LiveTracking';
import DriverPanel from './pages/driver/DriverPanel';
import ParentDashboard from './pages/parent/ParentDashboard';
import SuperAdminSetup from './pages/superadmin/SuperAdminSetup';
import TenantsManager from './pages/superadmin/TenantsManager';
import SuperUsersManager from './pages/superadmin/UsersManager';
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';

function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user?.role)) return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  const getHomeRedirect = () => {
    if (!isAuthenticated) return <Navigate to="/login" />;
    switch (user?.role) {
      case 'superadmin': return <Navigate to="/superadmin/setup" />;
      case 'admin': return <Navigate to="/admin" />;
      case 'driver': return <Navigate to="/driver" />;
      case 'parent': return <Navigate to="/parent" />;
      default: return <Navigate to="/login" />;
    }
  };

  return (
    <>
      <LanguageSwitcher />
      <Routes>
        <Route path="/login" element={isAuthenticated ? getHomeRedirect() : <Login />} />
        
        {/* Superadmin routes wrapped in layout */}
        <Route path="/superadmin/*" element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SuperAdminLayout>
              <Routes>
                <Route path="setup" element={<SuperAdminSetup />} />
                <Route path="tenants" element={<TenantsManager />} />
                <Route path="users" element={<SuperUsersManager />} />
              </Routes>
            </SuperAdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><UsersManager /></ProtectedRoute>} />
        <Route path="/admin/buses" element={<ProtectedRoute allowedRoles={['admin']}><BusesManager /></ProtectedRoute>} />
        <Route path="/admin/routes" element={<ProtectedRoute allowedRoles={['admin']}><RoutesManager /></ProtectedRoute>} />
        <Route path="/admin/students" element={<ProtectedRoute allowedRoles={['admin']}><StudentsManager /></ProtectedRoute>} />
        <Route path="/admin/history" element={<ProtectedRoute allowedRoles={['admin']}><TripHistory /></ProtectedRoute>} />
        <Route path="/admin/tracking" element={<ProtectedRoute allowedRoles={['admin']}><LiveTracking /></ProtectedRoute>} />
        <Route path="/driver" element={<ProtectedRoute allowedRoles={['driver']}><DriverPanel /></ProtectedRoute>} />
        <Route path="/parent" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
        <Route path="*" element={getHomeRedirect()} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
